import type { RowData } from '@tanstack/react-table';
import { NumberValue } from '../../Number';
import { NumberColumnFilter } from '../filter/NumberColumnFilter';
import type { ExtendedColumnDef, UnknownTextTree } from '../types';

type NumberColumnMeta<T extends RowData, TTexts> = Omit<
  NonNullable<ExtendedColumnDef<T, TTexts>['meta']>,
  'dataType' | 'fractionDigits' | 'step' | 'unit'
>;

type DistributiveOmit<T, TKey extends PropertyKey> = T extends unknown
  ? Omit<T, TKey>
  : never;

export type NumberColumnDef<
  T extends RowData = any,
  TTexts = never,
> = DistributiveOmit<ExtendedColumnDef<T, TTexts>, 'meta'> & {
  fractionDigits?: number;
  meta?: NumberColumnMeta<T, TTexts>;
  step?: number;
  unit?: string;
};

export const numberColumnDefaults: Partial<
  ExtendedColumnDef<any, UnknownTextTree>
> = {
  Filter: NumberColumnFilter,
  filterFn: 'inNumberRange',
  cell: ({ getValue, column }) => (
    <NumberValue
      value={getValue()}
      fractionDigits={column.columnDef.meta?.fractionDigits ?? 1}
    />
  ),
  sortUndefined: -1,
  meta: {
    enableAggregations: true,
    align: 'right',
  },
};

export const NumberColumn = <T extends RowData = any, TTexts = never>({
  fractionDigits,
  meta,
  step,
  unit,
  ...def
}: NumberColumnDef<T, TTexts>): ExtendedColumnDef<T, TTexts> =>
  ({
    ...numberColumnDefaults,
    ...def,
    meta: {
      ...numberColumnDefaults.meta,
      ...meta,
      dataType: 'number',
      ...(fractionDigits === undefined ? {} : { fractionDigits }),
      ...(step === undefined ? {} : { step }),
      ...(unit === undefined ? {} : { unit }),
    },
  }) as ExtendedColumnDef<T, TTexts>;
